// Appearance (the Mac's Settings ▸ Appearance, `AppSettings.appearance`): Match System, Paper or Lamplight. Match System
// follows the browser's colour scheme (app.css's `prefers-color-scheme` rules); Paper and Lamplight are forced for this
// browser as `data-theme="light"|"dark"` on the document, kept in localStorage. Contrast (contrast.ts) applies on top.
import { useSyncExternalStore } from 'react';

export type Appearance = 'system' | 'paper' | 'lamplight';

const KEY = 'ws:appearance';
const listeners = new Set<() => void>();

/** The browser bar's colour for each side (index.html's theme-color metas, which otherwise follow the system). */
const THEME_COLOR = { paper: '#B5502F', lamplight: '#1B1714' } as const;

function stored(): Appearance {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'paper' || value === 'lamplight' ? value : 'system';
  } catch {
    return 'system';
  }
}

/** Puts the choice on the document (at start-up, and whenever it changes). */
export function applyAppearance(mode: Appearance = stored()) {
  const root = document.documentElement;
  if (mode === 'system') delete root.dataset.theme;
  else root.dataset.theme = mode === 'paper' ? 'light' : 'dark';
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    const own = /dark/.test(meta.media) ? THEME_COLOR.lamplight : THEME_COLOR.paper;
    meta.content = mode === 'system' ? own : THEME_COLOR[mode];
  }
}

export function setAppearance(mode: Appearance) {
  try {
    if (mode === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    // kept for this page only
  }
  applyAppearance(mode);
  for (const fn of listeners) fn();
}

/** The appearance chosen in this browser. */
export function useAppearance(): Appearance {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    stored,
    () => 'system' as const,
  );
}
